// 목록 화면 상태 복원(뒤로 가기).
// 페이지·더보기 개수·보기 방식·정렬·검색어 등을 sessionStorage에 저장해 두고,
// 상세 화면에서 "돌아온 경우"에만 다시 채운다. 메뉴나 링크로 새로 들어오면 초기 상태로 시작한다.
import { watch, type Ref } from 'vue'
import { useRoute, type Router } from 'vue-router'

const STORAGE_PREFIX = 'list-state:'
const SCROLL_STORAGE_KEY = 'list-state-scroll'
const MAX_SCROLL_ENTRIES = 50

// ─── 돌아온 이동인지 판별 ─────────────────────────────────────────────────────
// 복원 대상 경로. 같은 경로 안에서 쿼리만 바뀌는 이동(router.replace 등)에는 유지하고,
// 다른 경로로 새로 이동하면 지운다.
let restoreTargetPath: string | null = null
let popPending = false

function isPageReload(): boolean {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    return nav?.type === 'reload'
  } catch {
    return false
  }
}

// router/index.ts에서 다른 beforeEach보다 먼저 등록한다.
export function setupListStateNavigation(router: Router): void {
  // 브라우저 뒤로/앞으로 가기
  router.options.history.listen((_to, _from, info) => {
    if (info.type === 'pop') popPending = true
  })

  router.beforeEach((to, from) => {
    const isInitial = from.matched.length === 0
    // 화면 안의 "뒤로"·브레드크럼이 바로 이전 방문 기록과 같은 주소로 이동하는 경우
    const isBackLink = !isInitial && window.history.state?.back === to.fullPath
    const restorable = popPending || isBackLink || (isInitial && isPageReload())
    popPending = false

    if (restorable) restoreTargetPath = to.path
    else if (to.path !== from.path) restoreTargetPath = null
  })
}

export function isRestoreNavigation(path: string): boolean {
  return restoreTargetPath === path
}

// ─── sessionStorage 헬퍼 (사용 불가 환경에서는 조용히 무시) ─────────────────────
function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch { /* 저장 실패 시 복원만 안 됨 */ }
}

function removeKey(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch { /* 무시 */ }
}

// ─── 목록 상태 저장·복원 ──────────────────────────────────────────────────────
interface SavedListState {
  fullPath: string
  values: Record<string, unknown>
}

// refs에 등록한 값을 바뀔 때마다 저장하고, 돌아온 경우 화면 생성 시점에 저장값으로 채운다.
// 반환값 restored: 복원한 값(없으면 null). 데이터가 비동기로 오는 화면은 이 값으로 추가 처리한다.
// matchFullPath: 쿼리(검색어·필터 등)까지 같을 때만 복원 (기본 true)
export function useListState(
  id: string,
  refs: Record<string, Ref<any>>,
  options: { matchFullPath?: boolean } = {},
): { restored: Record<string, unknown> | null } {
  const route = useRoute()
  const path = route.path
  const key = `${STORAGE_PREFIX}${path}:${id}`
  const matchFullPath = options.matchFullPath ?? true

  let restored: Record<string, unknown> | null = null
  if (isRestoreNavigation(path)) {
    const saved = readJson<SavedListState>(key)
    if (saved && (!matchFullPath || saved.fullPath === route.fullPath)) {
      restored = saved.values
      for (const [name, r] of Object.entries(refs)) {
        if (name in saved.values) r.value = saved.values[name]
      }
    }
  } else {
    removeKey(key)
  }

  const save = () => {
    // 다른 화면으로 이동하는 중(현재 route가 이미 바뀐 상태)에는 저장하지 않는다
    if (route.path !== path) return
    const values: Record<string, unknown> = {}
    for (const [name, r] of Object.entries(refs)) values[name] = r.value
    writeJson(key, { fullPath: route.fullPath, values } satisfies SavedListState)
  }
  watch(Object.values(refs), save, { deep: true })
  // 쿼리만 바뀌는 이동(검색어·필터를 URL에 반영)도 저장 기준 주소에 반영
  watch(() => route.fullPath, save)

  return { restored }
}

// ─── 스크롤 위치 저장·복원 ────────────────────────────────────────────────────
// 주소(fullPath)별 스크롤 위치. 내부 스크롤 영역(App.vue의 본문, Explorer 우측 목록 등)에 사용한다.
export function saveScrollPosition(scopeKey: string, top: number): void {
  const map = readJson<Record<string, number>>(SCROLL_STORAGE_KEY) ?? {}
  delete map[scopeKey]
  map[scopeKey] = Math.round(top)
  const keys = Object.keys(map)
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_SCROLL_ENTRIES))) delete map[k]
  writeJson(SCROLL_STORAGE_KEY, map)
}

export function getSavedScrollPosition(scopeKey: string): number | null {
  const map = readJson<Record<string, number>>(SCROLL_STORAGE_KEY)
  const top = map?.[scopeKey]
  return typeof top === 'number' ? top : null
}

// 목록 데이터가 비동기로 그려지므로, 내용 높이가 충분해질 때까지(최대 timeoutMs) 매 프레임 다시 시도한다.
// 그 사이 사용자가 직접 스크롤하면 중단한다.
export function restoreScrollPosition(el: HTMLElement, top: number, timeoutMs = 5000): void {
  if (top <= 0) {
    el.scrollTop = 0
    return
  }
  let stopped = false
  const stop = () => {
    stopped = true
    el.removeEventListener('wheel', stop)
    el.removeEventListener('touchstart', stop)
    el.removeEventListener('mousedown', stop)
    window.removeEventListener('keydown', stop)
  }
  el.addEventListener('wheel', stop, { passive: true })
  el.addEventListener('touchstart', stop, { passive: true })
  el.addEventListener('mousedown', stop)
  window.addEventListener('keydown', stop)

  const startedAt = performance.now()
  const tick = () => {
    if (stopped) return
    if (el.scrollHeight - el.clientHeight >= top) {
      el.scrollTop = top
      stop()
      return
    }
    // 시간 안에 높이가 다 차지 않으면 갈 수 있는 곳까지만 이동
    if (performance.now() - startedAt > timeoutMs) {
      el.scrollTop = top
      stop()
      return
    }
    requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}
